import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Smartphone, Zap, Tv, Ticket } from "lucide-react";

const SERVICE_ICONS = {
  AIRTIME: <Smartphone className="h-5 w-5 text-blue-500" />,
  ELECTRICITY: <Zap className="h-5 w-5 text-yellow-500" />,
  DSTV: <Tv className="h-5 w-5 text-purple-500" />,
  VOUCHER: <Ticket className="h-5 w-5 text-green-500" />,
};

const SERVICE_LABELS = {
  AIRTIME: 'Sell Airtime',
  ELECTRICITY: 'Sell Electricity',
  DSTV: 'DSTV',
  VOUCHER: 'Voucher',
};

const SERVICE_COLORS = {
  AIRTIME: 'bg-blue-500 hover:bg-blue-600',
  ELECTRICITY: 'bg-yellow-500 hover:bg-yellow-600',
  DSTV: 'bg-purple-500 hover:bg-purple-600',
  VOUCHER: 'bg-green-500 hover:bg-green-600',
};

const NETWORKS = [
  { code: 'VODACOM', label: 'Vodacom', color: 'bg-red-500' },
  { code: 'MTN', label: 'MTN', color: 'bg-yellow-400' },
  { code: 'CELLC', label: 'Cell C', color: 'bg-blue-800' },
  { code: 'TELKOM', label: 'Telkom', color: 'bg-blue-500' },
];

export const VasServiceDialog = ({
  serviceType, onClose,
  amount, setAmount,
  vasNetwork, setVasNetwork,
  vasMeterNumber, setVasMeterNumber,
  vasProcessing, vasResult,
  onAdd,
}) => {
  return (
    <Dialog open={!!serviceType} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {SERVICE_ICONS[serviceType]}
            {SERVICE_LABELS[serviceType]}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {serviceType === 'AIRTIME' && (
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Network</Label>
              <div className="grid grid-cols-4 gap-1.5">
                {NETWORKS.map(n => (
                  <Button key={n.code}
                    variant={vasNetwork === n.code ? "default" : "outline"}
                    size="sm"
                    onClick={() => setVasNetwork(n.code)}
                    className={vasNetwork === n.code ? `${n.color} text-white text-xs` : 'text-xs'}
                    data-testid={`network-${n.code}`}>
                    {n.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {serviceType === 'ELECTRICITY' && (
            <div>
              <Label className="text-xs text-gray-500">Meter Number</Label>
              <Input type="text" value={vasMeterNumber}
                onChange={(e) => setVasMeterNumber(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="Enter meter number"
                className="h-11 text-lg font-mono mt-1" autoFocus
                data-testid="vas-meter-input"
              />
            </div>
          )}

          <div>
            <Label className="text-xs text-gray-500">Amount (R)</Label>
            <Input type="number" step="0.01" min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && amount && !vasProcessing) onAdd();
              }}
              placeholder="0.00"
              className="text-2xl h-14 text-center font-bold mt-1"
              autoFocus={serviceType !== 'AIRTIME' && serviceType !== 'ELECTRICITY'}
              data-testid="vas-amount-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            {[10, 20, 50, 100, 200, 500].map(amt => (
              <Button key={amt} variant="outline" size="sm"
                onClick={() => setAmount(amt.toString())} className="h-9 font-semibold">
                R{amt}
              </Button>
            ))}
          </div>

          {serviceType === 'VOUCHER' && (
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              Note: Vouchers include a 5% markup
            </p>
          )}

          {vasResult && (
            <div className="bg-green-50 border border-green-200 rounded p-2 text-sm">
              <p className="font-semibold text-green-700">Purchase successful {vasResult.mock && '(TEST MODE)'}</p>
              {vasResult.token && <p className="font-mono text-xs mt-1">Token: {vasResult.token}</p>}
              {vasResult.reference && <p className="text-xs text-green-600">Ref: {vasResult.reference}</p>}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={vasProcessing}>Cancel</Button>
          <Button onClick={onAdd}
            disabled={!amount || parseFloat(amount) <= 0 || vasProcessing}
            className={SERVICE_COLORS[serviceType]}
            data-testid="vas-add-btn">
            {vasProcessing ? "Processing..." : "Sell"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
